import { ChevronDown, Loader2, Pause, Play } from "lucide-react"
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { AUDIO_PREVIEW_STOP_EVENT } from "@/components/ui/audio-preview-player"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { DropdownSearchInput } from "@/components/ui/dropdown-search-input"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import { dropVoiceLibraryCache, loadVoiceLibrary } from "@/lib/voice-library-cache"
import { voiceProviderLabel } from "@/lib/voice-providers"
import { type VoiceLibraryResponse, type VoiceSelection } from "@/lib/voices-api"

/** Строка секции «Голоса героев» (структурно совместима с TimelineVoiceOption). */
export type VoicePickerCharacterVoice = {
  kind: "voice" | "library_default"
  voice_id: number | null
  voice_name: string | null
  character_id: number
  character_name: string
  provider_kind: string
  provider_voice_id: string | null
  active: boolean
  preview_audio_url: string | null
}

type VoicePickerProps = {
  projectId: number
  value: VoiceSelection | null
  onChange: (value: VoiceSelection | null) => void
  /** Голоса героев проекта (включая синтетические строки «по умолчанию»). */
  characterVoices: VoicePickerCharacterVoice[]
  /** Ограничение по провайдерам (голоса героев и секции библиотек); не задано — все. */
  providerKinds?: string[]
  /**
   * Если задан вместе с providerKinds: голос героя чужого провайдера остаётся в списке,
   * но не выбирается, а под такими пунктами показывается эта фраза. Без него строка прячется.
   */
  unavailableHint?: string
  /** Первый пункт со значением null (например «Не менять голос»); не задан — пункта нет. */
  noneOptionLabel?: string
  disabled?: boolean
  /** Раскрыть список сразу при монтировании (вход через отдельную кнопку). */
  autoOpen?: boolean
  /** Подпись кнопки, когда значение не выбрано. */
  placeholder?: string
  /** Класс обёртки — сюда идут flex-размеры от родителя. */
  className?: string
  buttonClassName?: string
  dropdownDirection?: "up" | "down"
  ariaLabel?: string
}

/** Поиск показываем, когда опций больше этого порога. */
const SEARCH_THRESHOLD = 8
/** Порядок секций библиотек — как в плане: ElevenLabs, затем MiniMax. */
const LIBRARY_PROVIDER_ORDER = ["elevenlabs", "minimax"]

type PickerOption = {
  key: string
  /** null — пункт «без голоса» (noneOptionLabel). */
  value: VoiceSelection | null
  label: string
  /** Бейдж справа: провайдер или пометка «по умолчанию». */
  badge: string | null
  active: boolean
  previewUrl: string | null
  searchText: string
  /** Провайдер не умеет это действие: строка видна, выбрать её нельзя. */
  unavailable?: boolean
}

type PickerSection = {
  id: string
  /** Пустая строка — секция без заголовка (пункт «без голоса»). */
  label: string
  options: PickerOption[]
}

export function selectionKey(value: VoiceSelection | null): string | null {
  if (!value) return null
  return value.kind === "voice" ? `voice:${value.voice_id}` : `lib:${value.provider_kind}:${value.provider_voice_id}`
}

/** Значение селектора из строки голосов героев (null — строка без ссылки на голос). */
export function characterVoiceSelection(row: VoicePickerCharacterVoice): VoiceSelection | null {
  if (row.kind === "voice" && row.voice_id != null) return { kind: "voice", voice_id: row.voice_id }
  if (row.provider_voice_id) {
    return { kind: "library", provider_kind: row.provider_kind, provider_voice_id: row.provider_voice_id }
  }
  return null
}

/**
 * Селектор голоса для TTS (и далее STS/героев): сверху голоса героев, ниже —
 * библиотеки провайдеров (грузятся лениво при первом открытии); поиск и
 * предпрослушивание. Меню — порталом (см. useDropdownMenu), иначе его обрежет
 * overflow таймлайна.
 */
export function VoicePicker({
  projectId,
  value,
  onChange,
  characterVoices,
  providerKinds,
  unavailableHint,
  noneOptionLabel,
  disabled = false,
  autoOpen = false,
  placeholder,
  className,
  buttonClassName,
  dropdownDirection = "down",
  ariaLabel,
}: VoicePickerProps) {
  const { t } = useTranslation(["common"])
  const [open, setOpen] = useState(autoOpen)
  const [query, setQuery] = useState("")
  // Какую строку нажали. У «голоса по умолчанию» нескольких героев одно и то же
  // значение библиотеки, по нему строку не отличить.
  const [pickedKey, setPickedKey] = useState<string | null>(null)
  const [library, setLibrary] = useState<VoiceLibraryResponse | null>(null)
  const [libraryFailed, setLibraryFailed] = useState(false)
  const [libraryLoading, setLibraryLoading] = useState(false)

  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const previewInstanceId = useId()
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const [previewKey, setPreviewKey] = useState<string | null>(null)

  const stopPreview = useCallback(() => {
    previewAudioRef.current?.pause()
    setPreviewKey(null)
  }, [])

  // «Играет только один»: чужой старт (AudioPreviewPlayer или другой пикер) глушит наш.
  useEffect(() => {
    const onStopOthers = (event: Event) => {
      if ((event as CustomEvent<string>).detail === previewInstanceId) return
      stopPreview()
    }
    window.addEventListener(AUDIO_PREVIEW_STOP_EVENT, onStopOthers)
    return () => window.removeEventListener(AUDIO_PREVIEW_STOP_EVENT, onStopOthers)
  }, [previewInstanceId, stopPreview])

  const providerAllowed = useCallback(
    (kind: string) => providerKinds == null || providerKinds.includes(kind),
    [providerKinds],
  )

  const requestLibrary = useCallback(() => {
    setLibraryFailed(false)
    setLibraryLoading(true)
    loadVoiceLibrary(projectId)
      .then((data) => {
        const failed = data.failed_provider_kinds ?? []
        // Неполный каталог (часть провайдеров сбоила) показываем, но успехом не
        // считаем: из кеша убираем — он общий, и другой пикер не должен принять
        // недобор за полный каталог.
        if (failed.length > 0) dropVoiceLibraryCache(projectId)
        setLibrary(data)
        // «Повторить» показываем, только если сбоил провайдер, чьи голоса этот
        // пикер вообще показывает: в STS-селекторе лежачий MiniMax ничего не
        // отнимает, и жаловаться там не на что.
        setLibraryFailed(failed.some(providerAllowed))
        setLibraryLoading(false)
      })
      .catch(() => {
        setLibraryFailed(true)
        setLibraryLoading(false)
      })
  }, [projectId, providerAllowed])

  useEffect(() => {
    if (!open) {
      setQuery("")
      stopPreview()
      return
    }
    if (!library && !libraryLoading && !libraryFailed) requestLibrary()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- реагируем только на open: загрузку повторяет кнопка «повторить»
  }, [open])

  useEffect(() => stopPreview, [stopPreview])

  const libraryByKey = useMemo(() => {
    const map = new Map<string, { name: string; preview_url: string | null }>()
    for (const row of library?.voices ?? []) {
      map.set(`lib:${row.provider_kind}:${row.provider_voice_id}`, { name: row.name, preview_url: row.preview_url })
    }
    return map
  }, [library])

  const sections = useMemo<PickerSection[]>(() => {
    const characterOptions = characterVoices.flatMap<PickerOption>((row) => {
      const allowed = providerAllowed(row.provider_kind)
      // Синтетический дефолт чужой библиотеки не показываем даже с подсказкой:
      // в STS серые MiniMax-строки «по умолчанию» нечем выбрать. Настоящий голос
      // героя чужого провайдера при подсказке остаётся серым. Без подсказки (TTS)
      // чужой провайдер пропадает целиком.
      if (!allowed && (row.kind === "library_default" || !unavailableHint)) return []
      const selection = characterVoiceSelection(row)
      if (!selection) return []
      const key = selectionKey(selection) ?? ""
      const unavailable = !allowed
      if (row.kind === "library_default") {
        const enriched = libraryByKey.get(key)
        const name = row.voice_name ?? enriched?.name
        const defaultLabel = name ? `${t("voicePicker.defaultVoice")}: ${name}` : t("voicePicker.defaultVoice")
        return [
          {
            // Ключ уникален в пределах секции: одинаковый дефолт у нескольких героев
            // отличается character_id.
            key: `${key}:c${row.character_id}`,
            value: selection,
            label: `${row.character_name} — ${defaultLabel}`,
            badge: t("voicePicker.libraryBadge", { provider: voiceProviderLabel(row.provider_kind) }),
            active: false,
            previewUrl: row.preview_audio_url ?? enriched?.preview_url ?? null,
            searchText: `${row.character_name} ${name ?? ""} ${voiceProviderLabel(row.provider_kind)}`.toLowerCase(),
            unavailable,
          },
        ]
      }
      return [
        {
          key,
          value: selection,
          label: `${row.character_name} — ${row.voice_name ?? ""}`,
          badge: voiceProviderLabel(row.provider_kind),
          active: row.active,
          previewUrl: row.preview_audio_url,
          searchText:
            `${row.character_name} ${row.voice_name ?? ""} ${voiceProviderLabel(row.provider_kind)}`.toLowerCase(),
          unavailable,
        },
      ]
    })
    // Сначала те, что можно выбрать: пояснение тогда стоит сразу под серыми.
    const orderedCharacterOptions = [
      ...characterOptions.filter((option) => !option.unavailable),
      ...characterOptions.filter((option) => option.unavailable),
    ]

    const librarySections = LIBRARY_PROVIDER_ORDER.filter(providerAllowed).flatMap<PickerSection>((kind) => {
      const rows = (library?.voices ?? []).filter((row) => row.provider_kind === kind)
      if (rows.length === 0) return []
      return [
        {
          id: `library:${kind}`,
          label: t("voicePicker.librarySection", { provider: voiceProviderLabel(kind) }),
          options: rows.map((row) => ({
            key: `lib:${row.provider_kind}:${row.provider_voice_id}`,
            value: {
              kind: "library" as const,
              provider_kind: row.provider_kind,
              provider_voice_id: row.provider_voice_id,
            },
            label: row.name,
            badge: null,
            active: false,
            previewUrl: row.preview_url,
            searchText: `${row.name} ${row.description ?? ""} ${voiceProviderLabel(row.provider_kind)}`.toLowerCase(),
          })),
        },
      ]
    })

    // Пункт «без голоса» — своя безымянная секция первым делом: он вне групп,
    // но так его одинаково фильтрует поиск и рисует общий renderOption.
    const noneSection: PickerSection[] = noneOptionLabel
      ? [
          {
            id: "none",
            label: "",
            options: [
              {
                key: "none",
                value: null,
                label: noneOptionLabel,
                badge: null,
                active: false,
                previewUrl: null,
                searchText: noneOptionLabel.toLowerCase(),
              },
            ],
          },
        ]
      : []

    return [
      ...noneSection,
      ...(orderedCharacterOptions.length > 0
        ? [{ id: "characters", label: t("voicePicker.characterVoices"), options: orderedCharacterOptions }]
        : []),
      ...librarySections,
    ]
  }, [characterVoices, library, libraryByKey, noneOptionLabel, providerAllowed, t, unavailableHint])

  const totalOptions = useMemo(() => sections.reduce((sum, s) => sum + s.options.length, 0), [sections])
  const searchable = totalOptions > SEARCH_THRESHOLD

  const normalizedQuery = query.trim().toLowerCase()
  const visibleSections = useMemo(() => {
    if (!normalizedQuery) return sections
    return sections
      .map((section) => ({
        ...section,
        options: section.options.filter((o) => o.searchText.includes(normalizedQuery)),
      }))
      .filter((section) => section.options.length > 0)
  }, [sections, normalizedQuery])

  const selectedKey = selectionKey(value)
  useEffect(() => {
    if (pickedKey == null) return
    const picked = sections.flatMap((section) => section.options).find((option) => option.key === pickedKey)
    // Строка ещё не в списке (библиотека грузится) — ключ не сбрасываем.
    if (!picked) return
    if (selectionKey(picked.value) !== selectedKey) setPickedKey(null)
  }, [pickedKey, sections, selectedKey])
  // Сначала строка, по которой кликнули. Иначе — каталожная строка с тем же ключом,
  // что и значение. Синтетические дефолты героев ключ значения не носят (:c{id}),
  // поэтому без клика они не загораются все сразу.
  const optionSelected = (option: PickerOption) => {
    if (option.unavailable) return false
    if (pickedKey != null) return option.key === pickedKey
    if (value == null) return option.value == null
    return option.key === selectedKey
  }
  const selectedLabel = useMemo(() => {
    if (pickedKey) {
      for (const section of sections) {
        const match = section.options.find((option) => option.key === pickedKey)
        if (match) return match.label
      }
    }
    if (!value) return noneOptionLabel ?? null
    if (value.kind === "library") {
      // Библиотечное значение подписываем именем голоса, а не строкой героя:
      // синтетические строки «по умолчанию» разных героев несут то же значение,
      // и label первого попавшегося героя приписал бы выбор не тому персонажу.
      const enriched = libraryByKey.get(selectedKey ?? "")
      if (enriched) return enriched.name
      for (const section of sections) {
        const match = section.options.find((o) => o.key === selectedKey)
        if (match) return match.label
      }
      return t("voicePicker.defaultVoice")
    }
    for (const section of sections) {
      const match = section.options.find((o) => selectionKey(o.value) === selectedKey)
      if (match) return match.label
    }
    return null
  }, [pickedKey, value, sections, selectedKey, libraryByKey, noneOptionLabel, t])

  useEffect(() => {
    if (!open || !searchable) return
    const id = requestAnimationFrame(() => searchInputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open, searchable])

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    disabled,
    direction: dropdownDirection,
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    positionKey: `${totalOptions}:${libraryLoading}:${libraryFailed}`,
    resolveWidth: (rect) => ({
      minWidth: Math.max(rect.width, 288),
      width: "max-content",
      maxWidth: "min(26rem, calc(100vw - 16px))",
    }),
    onOutsideClick: () => setOpen(false),
  })

  const selectOption = (option: PickerOption) => {
    if (option.unavailable) return
    setPickedKey(option.key)
    onChange(option.value)
    setOpen(false)
  }

  const togglePreview = (option: PickerOption) => {
    const audio = previewAudioRef.current
    if (!audio || !option.previewUrl) return
    if (previewKey === option.key) {
      stopPreview()
      return
    }
    window.dispatchEvent(new CustomEvent(AUDIO_PREVIEW_STOP_EVENT, { detail: previewInstanceId }))
    audio.pause()
    audio.src = option.previewUrl
    void audio
      .play()
      .then(() => setPreviewKey(option.key))
      .catch(() => setPreviewKey(null))
  }

  const renderOption = (option: PickerOption) => (
    <li
      key={option.key}
      role="option"
      aria-disabled={option.unavailable || undefined}
      aria-selected={optionSelected(option)}
      onMouseDown={(e) => {
        e.preventDefault()
        selectOption(option)
      }}
      className={cn(
        "flex min-h-8 items-center gap-2 px-3 py-1.5 text-xs transition-colors",
        option.unavailable ? "cursor-not-allowed text-ui-text-muted" : "cursor-pointer hover:bg-ui-accent/25",
        !option.unavailable &&
          (optionSelected(option) ? "bg-ui-accent-soft text-ui-accent-text" : "text-ui-text-primary"),
      )}
    >
      <span className="min-w-0 flex-1 truncate">
        {option.label}
        {option.active ? (
          <span className={cn("ml-1.5 text-[10px]", option.unavailable ? "text-ui-text-muted" : "text-ui-accent-text")}>
            {t("voicePicker.activeMark")}
          </span>
        ) : null}
      </span>
      {option.badge ? (
        <span className="shrink-0 rounded border border-ui-border px-1 py-px text-[10px] leading-4 text-ui-text-muted">
          {option.badge}
        </span>
      ) : null}
      {option.previewUrl ? (
        <button
          type="button"
          aria-label={previewKey === option.key ? t("voicePicker.pausePreview") : t("voicePicker.playPreview")}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            togglePreview(option)
          }}
          className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-ui-text-secondary transition-colors hover:bg-ui-accent/20 hover:text-ui-accent-text"
        >
          {previewKey === option.key ? (
            <Pause className="h-3 w-3" fill="currentColor" />
          ) : (
            <Play className="h-3 w-3" fill="currentColor" />
          )}
        </button>
      ) : (
        // Пустой слот вместо кнопки — строки секции не «прыгают» по ширине.
        <span className="h-6 w-6 shrink-0" aria-hidden />
      )}
    </li>
  )

  const menu =
    open && !disabled && menuStyle && portalRoot ? (
      <ul
        ref={menuRef}
        role="listbox"
        data-panel-wheel-isolate
        aria-label={ariaLabel ?? t("voicePicker.voice")}
        style={menuStyle}
        className={cn(
          "max-h-72 overflow-auto rounded-lg border border-ui-border bg-ui-bg-elevated shadow-xl shadow-black/40",
          searchable ? "pb-1" : "py-1",
        )}
      >
        {searchable ? (
          <li role="none" className="sticky top-0 z-10">
            <DropdownSearchInput
              inputRef={searchInputRef}
              value={query}
              onChange={setQuery}
              placeholder={t("voicePicker.searchPlaceholder")}
              ariaLabel={t("voicePicker.searchPlaceholder")}
              onDismiss={() => setOpen(false)}
              onCommitFirst={() => {
                const first = visibleSections.flatMap((s) => s.options).find((option) => !option.unavailable)
                if (first) selectOption(first)
              }}
            />
          </li>
        ) : null}
        {visibleSections.map((section) => {
          const selectable = section.options.filter((option) => !option.unavailable)
          const unavailable = section.options.filter((option) => option.unavailable)
          return (
            <li key={section.id} role="none">
              {section.label ? (
                <div className="px-3 pt-2 pb-1 text-[10px] font-medium tracking-wide text-ui-text-muted uppercase">
                  {section.label}
                </div>
              ) : null}
              {selectable.length > 0 ? <ul role="none">{selectable.map(renderOption)}</ul> : null}
              {unavailable.length > 0 ? (
                <>
                  <ul role="none">{unavailable.map(renderOption)}</ul>
                  {unavailableHint ? (
                    <p className="px-3 pt-0.5 pb-1.5 text-[10px] leading-4 text-ui-text-muted">{unavailableHint}</p>
                  ) : null}
                </>
              ) : null}
            </li>
          )
        })}
        {normalizedQuery && visibleSections.length === 0 ? (
          <li className="px-3 py-2 text-xs text-ui-text-muted">{t("voicePicker.searchEmpty")}</li>
        ) : null}
        {!normalizedQuery && !libraryLoading && !libraryFailed && totalOptions === 0 ? (
          <li className="px-3 py-2 text-xs text-ui-text-muted">{t("voicePicker.noVoices")}</li>
        ) : null}
        {libraryLoading ? (
          <li className="flex items-center gap-2 px-3 py-2 text-xs text-ui-text-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("voicePicker.libraryLoading")}
          </li>
        ) : null}
        {libraryFailed ? (
          <li className="flex items-center gap-2 px-3 py-2 text-xs text-ui-text-muted">
            <span>{t("voicePicker.libraryFailed")}</span>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                requestLibrary()
              }}
              className="text-ui-accent-text hover:underline"
            >
              {t("voicePicker.retry")}
            </button>
          </li>
        ) : null}
      </ul>
    ) : null

  return (
    <div ref={rootRef} className={cn("relative min-w-0", className)}>
      <audio ref={previewAudioRef} preload="none" onEnded={() => setPreviewKey(null)} />
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? t("voicePicker.voice")}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "inline-flex w-full items-center gap-1 text-ui-text-primary disabled:cursor-not-allowed disabled:opacity-40",
          buttonClassName,
        )}
      >
        <span className="min-w-0 flex-1 truncate text-left">
          {selectedLabel ?? placeholder ?? t("voicePicker.voice")}
        </span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 shrink-0 text-ui-text-muted transition-transform", open && "rotate-180")}
        />
      </button>
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
    </div>
  )
}
